import React, { Component, Fragment } from 'react'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import * as R from 'ramda'

import Chart from '../Chart'
import colours from '../Chart/colours'
import OptionResponse from '../OptionResponse'
import ReportPageTitle from '../ReportPageTitle'
import OptionTitle from './OptionTitle'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import { sortedIndexArray, getSortDirection } from '../../../../../utils/helpers'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionFooter from '../QuestionFooter'
import DebugInfo from '../DebugInfo'
import QuestionEdit from '../QuestionEdit'
import GroupedRows from './GroupedRows'
import ChartSelector from '../ChartSelector'
import AiOverview from './AiOverview'

@observer
class DoubleAxis extends Component {
    constructor(props) {
        super(props)
        const {
            store, reportStore, question, dataKey, reportFilter,
        } = props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]
        this.question = question
        this.filteredCategories = R.pathOr([], ['yOptions'], data).reduce((filtered, option, index) => {
            if (option.isHidden) {
                filtered.push(index)
            }
            return filtered
        }, [])
        this.categories = question.optionSetY.options
            .map((option) => (
                getOptionTitle({
                    langs: option.langs,
                    locale: reportStore.locale,
                    defaultLocale: survey.metadata.defaultLocale,
                    initialLocale: question.metadata.initialLocale,
                })
            ))

        this.isMaxbiReport = survey.isMaxbi()
        this.indexArray = this.isMaxbiReport ? reportStore.filterResponses(dataKey, question)
            : Array.from(Array(question.optionSetY.options.length).keys())
        this.showChart = this.isMaxbiReport ? reportStore.showChart(question) : true
        this.state = {
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(question.optionSetY.options.length).keys()),
                sortedIndexArray: this.indexArray,
            },
            chartType: question.settings.report.graphType,
        }

        this.chartColours = [
            colours.getSerieColour('survey'),
            colours.getSerieColour('comparison'),
        ]
        Array.from(reportFilter.benchmarks).forEach(([key]) => {
            this.chartColours.push(colours.getSerieColour(key))
        })
        this.legends = {}

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                })
            },
        )

        this.chartType = question.settings.report.graphType
        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    componentWillUnmount() {
        const { reportStore, dataKey } = this.props
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    getQuestionSorting = () => {
        const { question } = this.props
        const { sorting } = this.state

        const { questionTypeCode } = question.metadata
        const hasNumericCodes = questionTypeCode === 'RT'
            ? true
            : R.pathOr(false, ['optionSetX', 'hasNumericCodes'], question)

        const chartType = hasNumericCodes
            ? this.chartType
            : 'stackedBar'

        return [sorting.sortedIndexArray, chartType]
    }

    getXResponse = (data, index, optionX) => {
        const { questionTypeCode } = data
        const id = optionX.toUpperCase()
        let response = 0
        if (Object.hasOwnProperty.call(data.xOptions, id)) {
            const optionResponse = data.xOptions[id]
            response = questionTypeCode === 'RT'
                ? optionResponse.numberOfResponses[index]
                : optionResponse.pctOfResponses[index].toFixed(2)
        }
        return response
    }

    renderFilter = (optionId, setXId) => {
        const { reportStore, question } = this.props
        const { showQuickFilter, quickFilters } = reportStore
        const ruleKey = `${question.id}_${optionId}_${setXId}`
        const hasFilter = quickFilters.has(ruleKey)
        const className = hasFilter
            ? 'fa-regular fa-square-check'
            : 'fa-regular fa-square'

        if (!showQuickFilter) return null

        return (
            <div className="quick-filter-check-container">
                <i
                    onClick={() => { reportStore.filterByOption(question, optionId, setXId) }}
                    className={className}
                />
            </div>
        )
    }

    colMapper = ({
        path, type, id, colorKey, setXId
    }, data, option, index) => {
        const {
            question, reportStore, store, reportFilter,
        } = this.props
        const { survey } = store
        let col = null
        if (type === 'pct') {
            const label = `${R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}%`
            col = (
                <td>
                    { label }
                </td>
            )
        } else if (type === 'OptionResponse') {
            col = (
                <td>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'center' }}>
                        {this.renderFilter(option.id, setXId)}
                        <OptionResponse
                            id={id(option.id)}
                            value={R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                            reportStore={reportStore}
                            reportFilter={reportFilter}
                            pct
                        />
                    </div>
                </td>
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    <OptionTitle
                        id={option.id}
                        reportStore={reportStore}
                        option={option}
                        locale={reportStore.locale}
                        defaultLocale={survey.metadata.defaultLocale}
                        initialLocale={question.metadata.initialLocale}
                        toggleOther={this.toggleOther}
                        data={data}
                    />
                </th>
            )
        } else if (type === 'val') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data)}
                </td>
            )
        } else if (type === 'avg') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                </td>
            )
        } else if (type === 'optionSelected') {
            if (R.pathOr(0, [...path.split('.'), index])(data) === 100) {
                col = (
                    <td>
                        <i className="fas fa-check" />
                    </td>
                )
            } else {
                col = (
                    <td />
                )
            }
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const {
            reportStore, question, store, dataKey,
        } = this.props
        const { survey } = store

        const oldSortBy = sorting.sortBy
        const locales = {
            locale: reportStore.locale,
            defaultLocale: survey.metadata.defaultLocale,
            initialLocale: question.metadata.initialLocale,
        }
        const { values, sortBy } = reportStore.sortDetails(dataKey, col, question.optionSetY.options, locales)

        sorting.sortBy = sortBy

        const indexArray = sortedIndexArray(values)
        sorting.direction = getSortDirection((sorting.sortBy === oldSortBy), sorting.direction)

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = indexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = indexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }
        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                !path && sortBy === 'label'
                || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    getChartCategories = () => {
        const { question, reportStore, store } = this.props
        const { survey } = store
        const { sorting } = this.state
        const { sortedIndexArray } = sorting

        return sortedIndexArray.map((sortedIndex) => (
            getOptionTitle({
                langs: question.optionSetY.options[sortedIndex].langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))
    }

    getOptionId = (optionIdX) => (optionIdY) => {
        const { question } = this.props
        return `${question.pageQuestionId}_${optionIdY}_${optionIdX}`
    }

    getColumnType = (option) => {
        let columnType = 'OptionResponse'

        if (option.fieldType === 'I') {
            columnType = 'avg'
        } else if (this.isMaxbiReport) {
            columnType = 'optionSelected'
        }

        return columnType
    }

    renderRows = (cols, filterRows) => {
        const { sorting } = this.state
        const { reportStore, question, dataKey } = this.props
        const data = reportStore.reportData.questions[dataKey]
        return sorting.sortedIndexArray.map((sortedIndex) => {
            const option = question.optionSetY.options[sortedIndex]
            return !filterRows || filterRows.has(option.id)
                ? (
                    <tr className="col" key={`option_${option.id}`}>
                        {
                            cols
                                .filter((col) => !col.hide)
                                .map((col) => (
                                    this.colMapper(
                                        col, data, option, sortedIndex,
                                    )
                                ))
                        }
                        <td className="tlast" />
                    </tr>
                ) : null
        })
    }

    renderGroupedRows = (cols) => {
        const { store, question, reportStore } = this.props
        return (
            <GroupedRows
                question={question}
                renderRows={this.renderRows}
                cols={cols}
                reportStore={reportStore}
                store={store}
            />
        )
    }

    generateDataTable = () => {
        const {
            store, reportStore, question, dataKey,
        } = this.props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]

        const r = []
        if (!question.optionSetX.hasNumericCodes) {
            console.log('stacked data')
            question.optionSetX.options.forEach((x) => {
                r.push({
                    name: getOptionTitle({
                        langs: x.langs,
                        locale: reportStore.locale,
                        defaultLocale: survey.metadata.defaultLocale,
                        initialLocale: question.metadata.initialLocale,
                    }),
                    id: x.id.toUpperCase(),
                    path: `xOptions.${x.id.toUpperCase()}.pctOfResponses`,
                    type: this.isMaxbiReport ? 'optionSelected' : 'pct',
                    colorKey: 'survey',
                })
            })
        }

        const benchmarkCols = question.metadata.isBenchmarked
            ? Array.from(reportStore.benchmarks2)
                .filter(([key]) => (R.hasPath(['aggregate', 'benchmarks', key.toLowerCase()], data)))
                .map(([key, { value }]) => ({
                    label: i18n.t(`report_${key}_benchmark`),
                    path: `aggregate.benchmarks.${key}`,
                    type: 'avg',
                    key,
                    colorKey: key,
                    hide: !value,
                    noData: !(R.hasPath(['aggregate', 'benchmarks', key.toLowerCase()], data)),
                    hideInLegend: !value,
                    chartHide: !['company', 'client', 'industry'].includes(key.toLowerCase()),
                }))
            : []

        const comparisonCols = Array.from(reportStore.yoyComparisons)
            .filter(([id]) => (R.hasPath(['aggregate', 'comparison', id.toUpperCase()])(data)))
            .map(([id, { label }]) => ({
                label: reportStore.comparisonTitle(label),
                path: `aggregate.comparison.${id.toUpperCase()}`,
                type: 'avg',
                colorKey: 'comparison',
            }))

        const optionSetXColumns = question.optionSetX.options.map((option) => ({
            label: reportStore.xAxisOptionTitle(question.optionSetX, question.questionTypeCode, option, this.question.metadata.initialLocale),
            id: this.getOptionId(option.id),
            setXId: option.id,
            path: `xOptions.${option.id.toUpperCase()}.pctOfResponses`,
            type: this.getColumnType(option),
            colorKey: 'survey',
            chartHide: (option.fieldType === 'I'),
            hideInLegend: (option.fieldType === 'I'),
        }))
        const averages = (question.optionSetX.hasNumericCodes || question.questionTypeCode === 'RT')
            ? [{
                label: 'Average',
                type: 'avg',
                path: 'aggregate.average',
                colorKey: 'survey',
            }]
            : []

        const cols = this.isMaxbiReport ? [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            ...optionSetXColumns,
        ] : [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_responses'),
                path: 'numberOfResponses',
                type: 'val',
            },
            ...optionSetXColumns,
            ...averages,
            ...comparisonCols,
            ...benchmarkCols,
        ]

        if (question.questionTypeCode === 'RT') {
            this.cols = averages
            console.log('check here ', JSON.stringify(this.cols, null, 4))
        } else {
            this.cols = question.optionSetX.hasNumericCodes ? [
                ...averages,
                ...comparisonCols,
                ...benchmarkCols,
            ]
                : r
        }

        return (
            <div style={{ overflowX: 'auto' }}>
                <table className="datatable question tablesorter tablesorter-default">
                    <thead>
                        <tr className="col tablesorter-headerRow">
                            {cols
                                .filter((col) => !col.hide)
                                .map((col) => (
                                    this.isMaxbiReport
                                        ? (
                                            <th className={`${col.class || ''} tablesorter-header`}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                </div>
                                            </th>
                                        ) : (
                                            <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                                <div className="tablesorter-header-inner">
                                                    {col.label}
                                                    {this.sortIcon(col)}
                                                </div>
                                            </th>
                                        )
                                ))}
                            <td className="tlast" />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            question.hasHeaderGroups
                                ? this.renderGroupedRows(cols)
                                : this.renderRows(cols)
                        }
                    </tbody>
                </table>
            </div>
        )
    }

    render() {
        const {
            store, question, reportStore, reportFilter, dataKey, pageId,
        } = this.props
        const { chartType } = this.state
        const { survey } = store
        const data = R.or(reportStore.reportData.questions[dataKey], {})

        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                formContent: R.pathOr(null, ['formContent', 'shortTitle'])(question),
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                {question.questionTypeCode === 'TB' && <AiOverview />}
                <div id={`questionData_${question.pageQuestionId}`}>
                    {this.generateDataTable()}
                </div>
                {this.showChart && data.hasResponses && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            stacked={!question.optionSetX.hasNumericCodes && question.questionTypeCode !== 'RT'}
                            colors={{}}
                            dataKey={dataKey}
                            percentages={!question.optionSetX.hasNumericCodes && question.questionTypeCode !== 'RT'}
                            reportFilter={reportFilter}
                            survey={survey}
                            question={question}
                            data={data}
                            series={this.cols}
                            sort={`${this.state.sorting.sortBy}_${this.state.sorting.direction}`}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            isBenchmarked={question.metadata.isBenchmarked}
                            pageQuestionId={question.pageQuestionId}
                            id={question.pageQuestionId}
                            chartType={chartType}
                            categorySet={question.optionSetY.options}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.kpiType}
                            categories={this.getChartCategories()}
                            reportStore={reportStore}
                            filtered={this.filteredCategories}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Panel>
        )
    }
}

export default DoubleAxis
